import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { AuthLayout } from "./AuthLayout";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function Activate() {
  const { activate } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const emailHint = params.get("email");
  const initialToken = params.get("token") || "";
  const [token, setToken] = useState(initialToken);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const hasPrefilled = useMemo(() => !!initialToken, [initialToken]);

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    setError("");
    const res = await activate(token.trim());
    setLoading(false);
    if (res.ok) {
      setSuccess(true);
      setTimeout(() => navigate("/dashboard/events", { replace: true }), 1200);
    } else {
      setError(res.error);
    }
  };

  useEffect(() => {
    // If token was in the URL, do NOT auto-submit — let the organiser click, so they can see the dev token confirmation.
    // This mirrors the real email link UX.
  }, []);

  return (
    <AuthLayout testid="activate-page" quote="Almost there. Confirm your account to publish your first event.">
      <div className="gt-label mb-3">Account activation</div>
      <h2 className="font-heading text-4xl font-black tracking-tighter mb-2">
        Activate your account
      </h2>
      <p className="text-[var(--gt-ink-2)] text-sm mb-8">
        {emailHint ? (
          <>We&apos;ve prepared an activation link for <span className="font-semibold text-[var(--gt-ink)]">{emailHint}</span>. Confirm below to finish signup.</>
        ) : (
          <>Paste the activation token from your signup response to activate your account.</>
        )}
      </p>

      {success ? (
        <div className="gt-card flex items-start gap-3" data-testid="activate-success">
          <CheckCircle2 className="text-[var(--gt-accent)] shrink-0 mt-0.5" size={20} />
          <div>
            <div className="font-heading font-bold text-lg">Account activated</div>
            <div className="text-sm text-[var(--gt-ink-2)]">Redirecting to your dashboard…</div>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-5" data-testid="activate-form">
          <div>
            <label className="gt-form-label" htmlFor="token">Activation token</label>
            <input
              id="token"
              className="gt-input font-mono text-xs"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste your activation token"
              required
              data-testid="activate-token-input"
            />
            {hasPrefilled && (
              <p className="text-xs text-[var(--gt-ink-3)] mt-2">
                Token pre-filled from your signup response. Click below to activate.
              </p>
            )}
          </div>

          {error && (
            <div className="text-sm text-[var(--gt-accent)] font-medium" data-testid="activate-error">
              {error}
            </div>
          )}

          <button type="submit" className="gt-btn-primary" disabled={loading || !token.trim()} data-testid="activate-submit-btn">
            {loading && <Loader2 size={16} className="animate-spin" />}
            Activate account
          </button>

          <div className="text-xs text-[var(--gt-ink-3)] mt-2">
            Already activated?{" "}
            <Link to="/login" className="text-[var(--gt-ink)] font-semibold underline underline-offset-4">Log in instead</Link>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
